import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import { getErrorMessage, formatCurrency, formatDate } from '@/lib/utils';
import { 
  Landmark, Plus, ArrowDownToLine, ArrowUpFromLine, 
  Loader2, Trash2, ChevronDown, ChevronUp, RefreshCw,
  Building2, Calendar, FileText, CircleDollarSign,
  TrendingUp, TrendingDown, Wallet, Edit
} from 'lucide-react';
import { BACKEND_URL } from '@/lib/api';

const API = `${BACKEND_URL}/api`;

const LoansLedger = ({ user }) => {
  const [loading, setLoading] = useState(true);
  const [loans, setLoans] = useState([]);
  const [summary, setSummary] = useState(null);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loadingTxns, setLoadingTxns] = useState(false);
  
  // Dialog states
  const [showCreateLoan, setShowCreateLoan] = useState(false);
  const [showEditLoan, setShowEditLoan] = useState(false);
  const [showDisburse, setShowDisburse] = useState(false);
  const [showRepay, setShowRepay] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Form states
  const [loanForm, setLoanForm] = useState({
    name: '',
    loan_type: 'term_loan',
    description: '',
    principal_amount: '',
    bank_name: '',
    loan_account_number: '',
    sanction_date: ''
  });
  const [disburseForm, setDisburseForm] = useState({
    amount: '',
    date: new Date().toISOString().split('T')[0],
    bank_account: '',
    remarks: ''
  });
  const [repayForm, setRepayForm] = useState({
    amount: '',
    date: new Date().toISOString().split('T')[0],
    bank_account: '',
    remarks: ''
  });

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchLoans = useCallback(async (selectedLoanId = null) => {
    setLoading(true);
    try {
      const [loansRes, summaryRes, banksRes] = await Promise.all([
        axios.get(`${API}/loans`, { headers }),
        axios.get(`${API}/loans/summary/all`, { headers }),
        axios.get(`${API}/bank-accounts`, { headers })
      ]);
      setLoans(loansRes.data);
      setSummary(summaryRes.data);
      setBankAccounts(banksRes.data);
      
      // Update selectedLoan with fresh data if one is selected
      if (selectedLoanId) {
        const updatedLoan = loansRes.data.find(l => l.id === selectedLoanId);
        if (updatedLoan) {
          setSelectedLoan(updatedLoan);
        }
      }
    } catch (error) {
      console.error('Failed to fetch loans:', error);
      toast.error(getErrorMessage(error, 'Failed to load loans'));
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchTransactions = useCallback(async (loanId) => {
    setLoadingTxns(true);
    try {
      const response = await axios.get(`${API}/loans/${loanId}/transactions`, { headers });
      setTransactions(response.data);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load transactions'));
    } finally {
      setLoadingTxns(false);
    }
  }, []);

  useEffect(() => {
    fetchLoans();
  }, [fetchLoans]);

  useEffect(() => {
    if (selectedLoan) {
      fetchTransactions(selectedLoan.id);
    }
  }, [selectedLoan, fetchTransactions]);

  const handleCreateLoan = async () => {
    if (!loanForm.name || !loanForm.principal_amount) {
      toast.error('Please fill required fields');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/loans`, {
        ...loanForm,
        principal_amount: parseFloat(loanForm.principal_amount)
      }, { headers });
      toast.success('Loan created successfully');
      setShowCreateLoan(false);
      setLoanForm({
        name: '', loan_type: 'term_loan', description: '',
        principal_amount: '', bank_name: '', loan_account_number: '', sanction_date: ''
      });
      fetchLoans();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create loan'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateLoan = async () => {
    if (!selectedLoan) return;
    
    setSubmitting(true);
    try {
      await axios.patch(`${API}/loans/${selectedLoan.id}`, {
        ...loanForm,
        principal_amount: loanForm.principal_amount ? parseFloat(loanForm.principal_amount) : undefined
      }, { headers });
      toast.success('Loan updated successfully');
      setShowEditLoan(false);
      fetchLoans();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update loan'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteLoan = async (loanId) => {
    if (!window.confirm('Are you sure you want to delete this loan?')) return;
    
    try {
      await axios.delete(`${API}/loans/${loanId}`, { headers });
      toast.success('Loan deleted');
      if (selectedLoan?.id === loanId) {
        setSelectedLoan(null);
        setTransactions([]);
      }
      fetchLoans();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete loan'));
    }
  };

  const handleDisburse = async () => {
    if (!selectedLoan || !disburseForm.amount || !disburseForm.bank_account) {
      toast.error('Please fill required fields');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/loans/${selectedLoan.id}/disburse`, {
        ...disburseForm,
        amount: parseFloat(disburseForm.amount)
      }, { headers });
      toast.success('Funds disbursed successfully');
      setShowDisburse(false);
      setDisburseForm({
        amount: '', date: new Date().toISOString().split('T')[0], bank_account: '', remarks: ''
      });
      fetchLoans(selectedLoan.id);
      fetchTransactions(selectedLoan.id);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to disburse funds'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleRepay = async () => {
    if (!selectedLoan || !repayForm.amount || !repayForm.bank_account) {
      toast.error('Please fill required fields');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/loans/${selectedLoan.id}/repay`, {
        ...repayForm,
        amount: parseFloat(repayForm.amount)
      }, { headers });
      toast.success('Repayment recorded successfully');
      setShowRepay(false);
      setRepayForm({
        amount: '', date: new Date().toISOString().split('T')[0], bank_account: '', remarks: ''
      });
      fetchLoans(selectedLoan.id);
      fetchTransactions(selectedLoan.id);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to record repayment'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteTransaction = async (txnId) => {
    if (!window.confirm('Delete this transaction? This will reverse the loan balance and bank transaction.')) return;
    
    try {
      await axios.delete(`${API}/loans/transactions/${txnId}`, { headers });
      toast.success('Transaction deleted and reversed');
      fetchLoans(selectedLoan?.id);
      if (selectedLoan) {
        fetchTransactions(selectedLoan.id);
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete transaction'));
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-green-100 text-green-800',
      closed: 'bg-gray-100 text-gray-800',
      pending: 'bg-yellow-100 text-yellow-800'
    };
    return <Badge className={styles[status] || styles.pending}>{status?.toUpperCase()}</Badge>;
  };

  const getLoanTypeLabel = (type) => {
    return type === 'term_loan' ? 'Term Loan (TL)' : 'Overdraft (OD)';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
            <Landmark className="w-7 h-7 text-indigo-600" />
            Loans Ledger
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Manage term loans and overdrafts
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" size="icon" onClick={fetchLoans} data-testid="refresh-loans">
            <RefreshCw className="w-4 h-4" />
          </Button>
          {user?.role === 'admin' && (
            <Button onClick={() => setShowCreateLoan(true)} data-testid="create-loan-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Loan
            </Button>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <SummaryCard 
            label="Total Principal" 
            value={summary.total_principal} 
            icon={CircleDollarSign}
            color="blue"
          />
          <SummaryCard 
            label="Total Disbursed" 
            value={summary.total_disbursed} 
            icon={ArrowDownToLine}
            color="green"
          />
          <SummaryCard 
            label="Total Repaid" 
            value={summary.total_repaid} 
            icon={ArrowUpFromLine}
            color="purple"
          />
          <SummaryCard 
            label="Outstanding Balance" 
            value={summary.total_outstanding} 
            icon={Wallet}
            color="orange"
          />
        </div>
      )}

      {/* Two Column Layout */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Loans List */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">All Loans ({loans.length})</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y max-h-[500px] overflow-y-auto">
              {loans.length === 0 ? (
                <div className="p-6 text-center text-gray-500">
                  No loans created yet. Click "Add Loan" to create one.
                </div>
              ) : (
                loans.map((loan) => (
                  <div 
                    key={loan.id}
                    className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${selectedLoan?.id === loan.id ? 'bg-indigo-50 border-l-4 border-indigo-500' : ''}`}
                    onClick={() => setSelectedLoan(loan)}
                    data-testid={`loan-item-${loan.name}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-lg">{loan.name}</span>
                        {getStatusBadge(loan.status)}
                      </div>
                      <span className="text-sm text-gray-500">{getLoanTypeLabel(loan.loan_type)}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-gray-500">Principal:</span>
                        <span className="ml-2 font-medium">{formatCurrency(loan.principal_amount)}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Outstanding:</span>
                        <span className={`ml-2 font-medium ${loan.current_balance > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                          {formatCurrency(loan.current_balance)}
                        </span>
                      </div>
                    </div>
                    {loan.bank_name && (
                      <div className="mt-1 text-xs text-gray-400">
                        {loan.bank_name} {loan.loan_account_number && `• ${loan.loan_account_number}`}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* Loan Details & Transactions */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">
                {selectedLoan ? `${selectedLoan.name} Details` : 'Select a Loan'}
              </CardTitle>
              {selectedLoan && user?.role === 'admin' && (
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      setLoanForm({
                        name: selectedLoan.name,
                        loan_type: selectedLoan.loan_type,
                        description: selectedLoan.description || '',
                        principal_amount: selectedLoan.principal_amount?.toString() || '',
                        bank_name: selectedLoan.bank_name || '',
                        loan_account_number: selectedLoan.loan_account_number || '',
                        sanction_date: selectedLoan.sanction_date || ''
                      });
                      setShowEditLoan(true);
                    }}
                    data-testid="edit-loan-btn"
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="text-red-600 hover:text-red-700"
                    onClick={() => handleDeleteLoan(selectedLoan.id)}
                    data-testid="delete-loan-btn"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {!selectedLoan ? (
              <div className="text-center py-12 text-gray-500">
                Click on a loan from the list to view details and transactions
              </div>
            ) : (
              <div className="space-y-4">
                {/* Loan Summary */}
                <div className="grid grid-cols-2 gap-3 p-4 bg-gray-50 rounded-lg">
                  <div>
                    <p className="text-xs text-gray-500">Principal</p>
                    <p className="font-semibold">{formatCurrency(selectedLoan.principal_amount)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Disbursed</p>
                    <p className="font-semibold text-green-600">{formatCurrency(selectedLoan.disbursed_amount)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Repaid</p>
                    <p className="font-semibold text-purple-600">{formatCurrency(selectedLoan.repaid_amount)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Outstanding</p>
                    <p className="font-semibold text-orange-600">{formatCurrency(selectedLoan.current_balance)}</p>
                  </div>
                </div>

                {/* Action Buttons */}
                {user?.role === 'admin' && selectedLoan.status !== 'closed' && (
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      className="flex-1 text-green-600 border-green-200 hover:bg-green-50"
                      onClick={() => setShowDisburse(true)}
                      disabled={selectedLoan.disbursed_amount >= selectedLoan.principal_amount}
                      data-testid="disburse-btn"
                    >
                      <ArrowDownToLine className="w-4 h-4 mr-2" />
                      Disburse Funds
                    </Button>
                    <Button 
                      variant="outline"
                      className="flex-1 text-purple-600 border-purple-200 hover:bg-purple-50"
                      onClick={() => setShowRepay(true)}
                      disabled={selectedLoan.current_balance <= 0}
                      data-testid="repay-btn"
                    >
                      <ArrowUpFromLine className="w-4 h-4 mr-2" />
                      Record Repayment
                    </Button>
                  </div>
                )}

                {/* Transactions */}
                <div>
                  <h4 className="font-medium mb-2 flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Transaction History
                    {loadingTxns && <Loader2 className="w-4 h-4 animate-spin" />}
                  </h4>
                  
                  {transactions.length === 0 ? (
                    <div className="text-center py-6 text-gray-500 text-sm bg-gray-50 rounded-lg">
                      No transactions yet
                    </div>
                  ) : (
                    <div className="border rounded-lg overflow-hidden max-h-[300px] overflow-y-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Date</TableHead>
                            <TableHead>Type</TableHead>
                            <TableHead>Amount</TableHead>
                            <TableHead>Bank</TableHead>
                            <TableHead></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {transactions.map((txn) => (
                            <TableRow key={txn.id}>
                              <TableCell className="text-sm">{formatDate(txn.date)}</TableCell>
                              <TableCell>
                                {txn.transaction_type === 'disbursement' ? (
                                  <Badge className="bg-green-100 text-green-800">
                                    <ArrowDownToLine className="w-3 h-3 mr-1" />
                                    Disbursed
                                  </Badge>
                                ) : (
                                  <Badge className="bg-purple-100 text-purple-800">
                                    <ArrowUpFromLine className="w-3 h-3 mr-1" />
                                    Repaid
                                  </Badge>
                                )}
                              </TableCell>
                              <TableCell className="font-medium">{formatCurrency(txn.amount)}</TableCell>
                              <TableCell className="text-sm text-gray-600">{txn.bank_account}</TableCell>
                              <TableCell>
                                {user?.role === 'admin' && (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 text-red-500 hover:text-red-700"
                                    onClick={() => handleDeleteTransaction(txn.id)}
                                    data-testid={`delete-txn-${txn.id}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create Loan Dialog */}
      <Dialog open={showCreateLoan} onOpenChange={setShowCreateLoan}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Loan</DialogTitle>
            <DialogDescription>Add a new loan account to track</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Loan Name *</Label>
                <Input 
                  placeholder="e.g., L1-TL"
                  value={loanForm.name}
                  onChange={(e) => setLoanForm({...loanForm, name: e.target.value})}
                  data-testid="loan-name-input"
                />
              </div>
              <div>
                <Label>Loan Type *</Label>
                <Select 
                  value={loanForm.loan_type} 
                  onValueChange={(v) => setLoanForm({...loanForm, loan_type: v})}
                >
                  <SelectTrigger data-testid="loan-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="term_loan">Term Loan (TL)</SelectItem>
                    <SelectItem value="overdraft">Overdraft (OD)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Principal Amount *</Label>
              <Input 
                type="number"
                placeholder="Enter amount"
                value={loanForm.principal_amount}
                onChange={(e) => setLoanForm({...loanForm, principal_amount: e.target.value})}
                data-testid="loan-amount-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Lending Bank</Label>
                <Input 
                  placeholder="e.g., HDFC Bank"
                  value={loanForm.bank_name}
                  onChange={(e) => setLoanForm({...loanForm, bank_name: e.target.value})}
                />
              </div>
              <div>
                <Label>Loan Account No.</Label>
                <Input 
                  placeholder="Account number"
                  value={loanForm.loan_account_number}
                  onChange={(e) => setLoanForm({...loanForm, loan_account_number: e.target.value})}
                />
              </div>
            </div>
            <div>
              <Label>Sanction Date</Label>
              <Input 
                type="date"
                value={loanForm.sanction_date}
                onChange={(e) => setLoanForm({...loanForm, sanction_date: e.target.value})}
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea 
                placeholder="Additional notes..."
                value={loanForm.description}
                onChange={(e) => setLoanForm({...loanForm, description: e.target.value})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateLoan(false)}>Cancel</Button>
            <Button onClick={handleCreateLoan} disabled={submitting} data-testid="save-loan-btn">
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Create Loan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Loan Dialog */}
      <Dialog open={showEditLoan} onOpenChange={setShowEditLoan}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Loan</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Loan Name</Label>
                <Input 
                  value={loanForm.name}
                  onChange={(e) => setLoanForm({...loanForm, name: e.target.value})}
                />
              </div>
              <div>
                <Label>Principal Amount</Label>
                <Input 
                  type="number"
                  value={loanForm.principal_amount}
                  onChange={(e) => setLoanForm({...loanForm, principal_amount: e.target.value})}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Lending Bank</Label>
                <Input 
                  value={loanForm.bank_name}
                  onChange={(e) => setLoanForm({...loanForm, bank_name: e.target.value})}
                />
              </div>
              <div>
                <Label>Loan Account No.</Label>
                <Input 
                  value={loanForm.loan_account_number}
                  onChange={(e) => setLoanForm({...loanForm, loan_account_number: e.target.value})}
                />
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea 
                value={loanForm.description}
                onChange={(e) => setLoanForm({...loanForm, description: e.target.value})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditLoan(false)}>Cancel</Button>
            <Button onClick={handleUpdateLoan} disabled={submitting}>
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Disburse Funds Dialog */}
      <Dialog open={showDisburse} onOpenChange={setShowDisburse}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowDownToLine className="w-5 h-5 text-green-600" />
              Disburse Loan Funds
            </DialogTitle>
            <DialogDescription>
              Transfer funds from {selectedLoan?.name} to bank account
            </DialogDescription>
          </DialogHeader>
          {selectedLoan && (
            <div className="space-y-4">
              <div className="p-3 bg-green-50 rounded-lg text-sm">
                <p className="text-gray-600">
                  Available to disburse: <span className="font-semibold text-green-700">
                    {formatCurrency(selectedLoan.principal_amount - selectedLoan.disbursed_amount)}
                  </span>
                </p>
              </div>
              <div>
                <Label>Amount *</Label>
                <Input 
                  type="number"
                  placeholder="Enter amount"
                  value={disburseForm.amount}
                  onChange={(e) => setDisburseForm({...disburseForm, amount: e.target.value})}
                  data-testid="disburse-amount-input"
                />
              </div>
              <div>
                <Label>Transfer To Bank Account *</Label>
                <Select 
                  value={disburseForm.bank_account} 
                  onValueChange={(v) => setDisburseForm({...disburseForm, bank_account: v})}
                >
                  <SelectTrigger data-testid="disburse-bank-select">
                    <SelectValue placeholder="Select bank account" />
                  </SelectTrigger>
                  <SelectContent>
                    {bankAccounts.map((acc) => {
                      const accountName = typeof acc === 'string' ? acc : acc.bank_account;
                      return (
                        <SelectItem key={accountName} value={accountName}>
                          {accountName}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Date *</Label>
                <Input 
                  type="date"
                  value={disburseForm.date}
                  onChange={(e) => setDisburseForm({...disburseForm, date: e.target.value})}
                />
              </div>
              <div>
                <Label>Remarks</Label>
                <Input 
                  placeholder="Optional notes"
                  value={disburseForm.remarks}
                  onChange={(e) => setDisburseForm({...disburseForm, remarks: e.target.value})}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDisburse(false)}>Cancel</Button>
            <Button 
              onClick={handleDisburse} 
              disabled={submitting}
              className="bg-green-600 hover:bg-green-700"
              data-testid="confirm-disburse-btn"
            >
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Disburse Funds
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Repay Loan Dialog */}
      <Dialog open={showRepay} onOpenChange={setShowRepay}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowUpFromLine className="w-5 h-5 text-purple-600" />
              Record Loan Repayment
            </DialogTitle>
            <DialogDescription>
              Record principal repayment for {selectedLoan?.name}
            </DialogDescription>
          </DialogHeader>
          {selectedLoan && (
            <div className="space-y-4">
              <div className="p-3 bg-purple-50 rounded-lg text-sm">
                <p className="text-gray-600">
                  Outstanding balance: <span className="font-semibold text-purple-700">
                    {formatCurrency(selectedLoan.current_balance)}
                  </span>
                </p>
              </div>
              <div>
                <Label>Repayment Amount *</Label>
                <Input 
                  type="number"
                  placeholder="Enter amount"
                  value={repayForm.amount}
                  onChange={(e) => setRepayForm({...repayForm, amount: e.target.value})}
                  data-testid="repay-amount-input"
                />
              </div>
              <div>
                <Label>Pay From Bank Account *</Label>
                <Select 
                  value={repayForm.bank_account} 
                  onValueChange={(v) => setRepayForm({...repayForm, bank_account: v})}
                >
                  <SelectTrigger data-testid="repay-bank-select">
                    <SelectValue placeholder="Select bank account" />
                  </SelectTrigger>
                  <SelectContent>
                    {bankAccounts.map((acc) => {
                      const accountName = typeof acc === 'string' ? acc : acc.bank_account;
                      return (
                        <SelectItem key={accountName} value={accountName}>
                          {accountName}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Date *</Label>
                <Input 
                  type="date"
                  value={repayForm.date}
                  onChange={(e) => setRepayForm({...repayForm, date: e.target.value})}
                />
              </div>
              <div>
                <Label>Remarks</Label>
                <Input 
                  placeholder="Optional notes"
                  value={repayForm.remarks}
                  onChange={(e) => setRepayForm({...repayForm, remarks: e.target.value})}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRepay(false)}>Cancel</Button>
            <Button 
              onClick={handleRepay} 
              disabled={submitting}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="confirm-repay-btn"
            >
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Record Repayment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Summary Card Component
const SummaryCard = ({ label, value, icon: Icon, color }) => {
  const colorClasses = {
    green: 'bg-green-50 text-green-600 border-green-200',
    blue: 'bg-blue-50 text-blue-600 border-blue-200',
    purple: 'bg-purple-50 text-purple-600 border-purple-200',
    orange: 'bg-orange-50 text-orange-600 border-orange-200',
  };

  const classes = colorClasses[color] || colorClasses.blue;
  const [bgColor, textColor, borderColor] = classes.split(' ');

  return (
    <div className={`rounded-xl p-4 border-2 ${bgColor} ${borderColor}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-5 h-5 ${textColor}`} />
        <span className="text-sm text-gray-600">{label}</span>
      </div>
      <p className={`text-xl md:text-2xl font-bold ${textColor}`}>
        {formatCurrency(value)}
      </p>
    </div>
  );
};

export default LoansLedger;
